// @flow
import { useMemo } from 'react';
import { useQuery } from 'urql';
import type { TypeaheadItem } from '../types';
import LocationThumbnailQuery from './LocationThumbnailQuery.gql';
import getImageSources from '../getImageSources';
import UserAvatarQuery from './UserAvatarQuery.gql';

/**
 * The maximum number of images to request for a single update. React hooks such as useQuery cannot
 * be called in a loop, so we need to define a max number.
 */
const MAX_IMAGES = 10;

/**
 * @return the given search result, with an image fetched from GraphQL if applicable
 */
const useSearchResultWithImage = (result: ?TypeaheadItem): ?TypeaheadItem => {
  // For locations, use the primary thumbnail
  const [locationResponse] = useQuery({
    query: LocationThumbnailQuery,
    ...(result != null && result.details.type === 'LOCATION'
      ? { variables: { locationId: result.details.locationId } }
      : { variables: { locationId: -1 }, pause: true }),
  });

  // For users, use the their avatar
  const [userResponse] = useQuery({
    query: UserAvatarQuery,
    ...(result != null && result.details.type === 'USER'
      ? { variables: { username: result.details.username } }
      : { variables: { username: '' }, pause: true }),
  });

  let gqlImage;
  if (result?.details?.type === 'LOCATION') {
    if (!locationResponse.fetching && !locationResponse.error) {
      gqlImage = locationResponse.data?.locations?.[0]?.thumbnail;
    }
  } else if (result?.details?.type === 'USER') {
    if (!userResponse.fetching && !userResponse.error) {
      gqlImage = userResponse.data?.memberProfile?.avatar;
    }
  }

  const resultWithImage = useMemo(() => {
    if (result == null) {
      return null;
    }

    const image = getImageSources(gqlImage);

    if (image != null) {
      return { ...result, image };
    }

    return result;
  }, [result, gqlImage]);

  return resultWithImage;
};

/**
 * A hook that annotates search results with location thumbnails and user avatars.
 *
 * The typeahead API does not include this information, so a second request needs to be made. When
 * this is moved to GraphQL, this data dependency can be more efficiently expressed through a
 * nested query.
 */
const useSearchResultsWithImages = (results: TypeaheadItem[]): TypeaheadItem[] => {
  const resultsWithImages = [];

  // Hack! React hooks must be called the same number of times in the same order each render. We'll
  // always do MAX_IMAGES hooks, and just pause the ones we don't need. This will no longer be
  // necessary when this data dependency is expressed through a nested GraphQL query.
  for (let i = 0; i < MAX_IMAGES; i++) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const resultWithImage = useSearchResultWithImage(results[i]);
    if (resultWithImage != null) {
      resultsWithImages.push(resultWithImage);
    }
  }

  return resultsWithImages;
};

export default useSearchResultsWithImages;
